/*
  Calendar sign-in, in-app.

  Replaces the standalone CLAP tool (clap-calendar-login-access-platform), where
  an operator ran a local Node app, opened a URL by hand, and copy-pasted a
  base64 blob into the Encoded Access Token box.

  The round trip:

    1. ELM asks EMMS (through ELM's /emms/piggyback proxy) for a consent URL,
       telling it which origin this tab is on.
    2. A popup opens at that URL. The user consents at Google / Microsoft.
    3. The provider redirects the popup to EMMS - the redirect URI belongs to
       EMMS, not to ELM, which is why ELM's dev port can move around freely.
    4. EMMS redeems the code for tokens with the client secret it holds and
       parks the result. It sends the popup on to public/oauth-callback.html
       here with only a short-lived, single-use handoff code, which that page
       hands to this tab.
    5. This tab - logged in, so EMMS knows its tenant and user - calls
       calendar/oauth/finalize with the code. EMMS saves the credential only
       if this is the tenant and user that started the sign-in, so a consent
       link opened by anyone else can't land a calendar in this tenant.

  Neither the client secret, the authorization code nor any token is ever
  handled here.
*/

import axios from "axios";

const MESSAGE_SOURCE = "elm-calendar-oauth";
const CONSENT_TIMEOUT_MS = 5 * 60 * 1000;
const POPUP_FEATURES = "width=600,height=760,menubar=no,toolbar=no,noopener=no";

export const CALENDAR_OAUTH_TYPES = ["Google Calendar", "Office 365"];

export const isOAuthCalendarType = (calendarType) =>
  CALENDAR_OAUTH_TYPES.includes(calendarType);

export const providerLabel = (calendarType) =>
  calendarType === "Office 365" ? "Microsoft" : "Google";

const piggyback = async (requestBody, requestConfig) => {
  const api = localStorage.getItem("api");
  const response = await axios.post(
    `${api}/emms/piggyback`,
    requestBody,
    requestConfig
  );
  return response?.data;
};

/*
  Wait for oauth-callback.html to hand the token over.

  Resolves only on a result carrying our source marker and echoing the exact
  state we issued - an unguessable nonce signed by EMMS - so a stray message
  from another frame or an unrelated popup can't complete someone's sign-in.
*/
const waitForOAuthResult = (popup, expectedState) =>
  new Promise((resolve, reject) => {
    let settled = false;
    let closeWatch;

    /*
      The callback page reaches this tab over a BroadcastChannel rather than
      through window.opener: Microsoft's consent screen sets
      Cross-Origin-Opener-Policy, which severs the popup's opener and takes
      any postMessage that would have ridden on it with it. The message
      listener below stays as a fallback for providers that don't sever it.
    */
    let channel = null;
    try {
      channel = new BroadcastChannel(MESSAGE_SOURCE);
      channel.onmessage = (event) => handlePayload(event.data);
    } catch (e) {
      /* no BroadcastChannel - the message listener still covers it */
    }

    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      if (channel) channel.close();
      clearInterval(closeWatch);
      clearTimeout(timeout);
    };

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    };

    function onMessage(event) {
      if (event.origin !== window.location.origin) return;
      handlePayload(event.data);
    }

    function handlePayload(data) {
      if (!data || data.source !== MESSAGE_SOURCE) return;
      if (data.state !== expectedState) return;

      if (data.error) {
        finish(
          reject,
          new Error(
            data.errorDescription ||
              (data.error === "access_denied"
                ? "Sign-in was cancelled."
                : data.error)
          )
        );
        return;
      }
      if (!data.handoffCode) {
        finish(reject, new Error("The sign-in did not return a result."));
        return;
      }
      finish(resolve, data);
    }

    const timeout = setTimeout(() => {
      finish(reject, new Error("Sign-in timed out. Please try again."));
    }, CONSENT_TIMEOUT_MS);

    // The callback page posts its message and then closes itself, so a closed
    // popup is only an error if no message arrived first. The small delay lets
    // an in-flight message land before this fires.
    //
    // Google's consent screen sets Cross-Origin-Opener-Policy, which puts the
    // popup in a separate agent cluster while it's on accounts.google.com and
    // makes Chrome log a "would block the window.closed call" warning. `closed`
    // is still meant to be readable cross-origin per spec, but if a browser
    // ever does throw here, treat that as inconclusive rather than as closed -
    // the postMessage from oauth-callback.html (which isn't affected by COOP)
    // and the overall timeout are the real backstops.
    closeWatch = setInterval(() => {
      let isClosed = false;
      try {
        isClosed = Boolean(popup && popup.closed);
      } catch (e) {
        return;
      }
      if (isClosed) {
        setTimeout(() => {
          finish(
            reject,
            new Error("The sign-in window was closed before it finished.")
          );
        }, 400);
      }
    }, 500);

    window.addEventListener("message", onMessage);
  });

/**
 * Run the whole consent round trip.
 *
 * MUST be called synchronously from a click handler: the popup is opened before
 * the first await so the browser attributes it to the user's gesture. Opening it
 * after an await gets it blocked.
 *
 * EMMS saves the credential when this tab finalizes the sign-in; no token
 * comes back to the browser, only the saved credential's id and the account's
 * calendars.
 *
 * @param {string}  calendarType    "Google Calendar" | "Office 365"
 * @param {string}  credentialName  name for a new credential
 * @param {string}  calendarId      credential to re-authenticate, instead of a name
 * @param {string}  emmsId          the selected EMMS, for the piggyback proxy
 * @param {object}  emmsHeaders     emmsheaders?.emmsheaders?.headers from Redux
 * @param {object}  requestConfig   the axios config from Redux state.header
 * @returns {Promise<{calendarCredentialId: string, credentialName: string, calendars: Array, hasRefreshToken: boolean}>}
 */
export const runCalendarOAuth = async ({
  calendarType,
  credentialName,
  calendarId,
  emmsId,
  emmsHeaders,
  requestConfig,
}) => {
  if (!isOAuthCalendarType(calendarType)) {
    throw new Error(`${calendarType} does not use OAuth sign-in.`);
  }

  // Opened first, before anything async, or the popup blocker eats it.
  const popup = window.open("", "elm-calendar-oauth", POPUP_FEATURES);
  if (!popup) {
    throw new Error(
      "Your browser blocked the sign-in window. Allow pop-ups for this site and try again."
    );
  }

  try {
    /*
      ELM's piggyback builds the upstream query string by plain concatenation,
      without encoding. Pre-encoding here keeps the URL well-formed; Express
      decodes it once on the EMMS side, so the value arrives intact.
    */
    const authorize = await piggyback(
      {
        path: "calendar/oauth/authorize",
        query: {
          provider: encodeURIComponent(calendarType),
          // The provider redirects to EMMS, not here, so EMMS needs to be
          // told where to send the result on to afterwards.
          openerOrigin: encodeURIComponent(window.location.origin),
          ...(calendarId
            ? { calendarId: encodeURIComponent(calendarId) }
            : { credentialName: encodeURIComponent(credentialName || "") }),
        },
        emmsId: `${emmsId}`,
        body: {},
        requestType: "GET",
        headers: emmsHeaders,
      },
      requestConfig
    );

    if (!authorize?.authUrl || !authorize?.state) {
      throw new Error(
        "EMMS did not return a sign-in URL. Check the calendar OAuth settings in the EMMS .env."
      );
    }

    popup.location.href = authorize.authUrl;

    const { handoffCode } = await waitForOAuthResult(popup, authorize.state);

    const result = await piggyback(
      {
        path: "calendar/oauth/finalize",
        query: {},
        emmsId: `${emmsId}`,
        body: { handoffCode, state: authorize.state },
        requestType: "POST",
        headers: emmsHeaders,
      },
      requestConfig
    );

    if (!result?.calendarCredentialId) {
      throw new Error("The calendar credential was not saved.");
    }

    return {
      calendarCredentialId: result.calendarCredentialId,
      credentialName: result.credentialName,
      calendars: Array.isArray(result.calendars) ? result.calendars : [],
      hasRefreshToken: result.hasRefreshToken !== false,
      provider: result.provider,
    };
  } finally {
    // Whatever happened, don't leave an orphaned popup behind.
    try {
      if (popup && !popup.closed) popup.close();
    } catch (e) {
      /* cross-origin popup we no longer control - nothing to do */
    }
  }
};

export default runCalendarOAuth;
